"use client";

import { useMemo, useState } from "react";
import {
  CATEGORIES,
  FILTERS,
  photoUrl,
  type Badge,
  type Dish,
  type Tag,
} from "../lib/menu";

const BADGE_CLASS: Record<Badge, string> = {
  "Le Grand Classique": "badge badge--classic",
  "Végétarien": "badge badge--veg",
  "Poulet Halal": "badge badge--info",
  Prestige: "badge badge--sig",
  "Signature Maison": "badge badge--sig",
};

type Row = Dish & { categoryLabel: string };

export default function Menu() {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const [tag, setTag] = useState<Tag | null>(null);
  /** Plat déplié : la photo ne s'affiche qu'à la demande. */
  const [openRow, setOpenRow] = useState<string | null>(null);

  const filtering = tag !== null;

  const rows = useMemo<Row[]>(() => {
    // Sans filtre : la catégorie courante. Avec filtre : toute la carte.
    if (!tag) {
      const current = CATEGORIES.find((c) => c.id === active) ?? CATEGORIES[0];
      return current.dishes.map((d) => ({
        ...d,
        categoryLabel: current.label,
      }));
    }

    return CATEGORIES.flatMap((c) =>
      c.dishes
        .filter((d) => (d.tags ?? []).includes(tag))
        .map((d) => ({ ...d, categoryLabel: c.label }))
    );
  }, [active, tag]);

  const cat = CATEGORIES.find((c) => c.id === active) ?? CATEGORIES[0];

  return (
    <>
      {/* ---------------------------------- Filtres rapides */}
      <div className="menubar">
        <div className="chips" role="group" aria-label="Filtres rapides">
          <button
            type="button"
            className={`chip${tag === null ? " chip--on" : ""}`}
            aria-pressed={tag === null}
            onClick={() => setTag(null)}
          >
            Tout
          </button>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`chip${tag === f.id ? " chip--on" : ""}`}
              aria-pressed={tag === f.id}
              onClick={() => setTag(tag === f.id ? null : f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---------------------------------- Onglets éditoriaux */}
      {!filtering && (
        <div className="tabs">
          <div
            className="tabs__track"
            role="tablist"
            aria-label="Catégories de la carte"
          >
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                id={`tab-${c.id}`}
                aria-selected={c.id === active}
                aria-controls="menu-panel"
                className={`tab${c.id === active ? " tab--on" : ""}`}
                onClick={() => {
                  setActive(c.id);
                  setOpenRow(null);
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        id="menu-panel"
        role="tabpanel"
        aria-labelledby={filtering ? undefined : `tab-${cat.id}`}
        tabIndex={-1}
      >
        {filtering && (
          <div className="results">
            <p className="results__count" aria-live="polite">
              {rows.length === 0
                ? "Aucun plat ne correspond"
                : `${rows.length} plat${rows.length > 1 ? "s" : ""} trouvé${
                    rows.length > 1 ? "s" : ""
                  }`}
            </p>
            <button
              type="button"
              className="results__reset"
              onClick={() => setTag(null)}
            >
              Revenir à la carte
            </button>
          </div>
        )}

        {rows.length === 0 ? (
          <div className="empty">
            Aucun plat ne correspond à ce filtre. Revenez à la carte complète
            pour tout parcourir.
          </div>
        ) : (
          /* La clé change au changement de catégorie : l'animation
             "fade & slide" se rejoue. */
          <div className="dishlist" key={filtering ? "filtre" : active}>
            {rows.map((d) => {
              const open = openRow === d.name;
              /* Sans photo, la ligne n'est pas dépliable : ni "+", ni clic. */
              const expandable = Boolean(d.photo);

              const inner = (
                <>
                  <span className="row__main">
                    <span className="row__name">{d.name}</span>

                    <span className="row__right">
                      <span className="row__price">{d.price}</span>
                      {expandable && (
                        <span className="row__toggle" aria-hidden="true" />
                      )}
                    </span>

                    <span className="row__desc">{d.description}</span>

                    {d.badges && d.badges.length > 0 && (
                      <span className="row__tags">
                        {d.badges.map((b) => (
                          <span key={b} className={BADGE_CLASS[b]}>
                            {b}
                          </span>
                        ))}
                      </span>
                    )}

                    {filtering && (
                      <span className="row__cat">{d.categoryLabel}</span>
                    )}
                  </span>

                  {/* Photo visible uniquement si le client déplie le plat */}
                  {expandable && (
                    <span
                      className={`row__panel${open ? " row__panel--open" : ""}`}
                    >
                      <span className="row__photo">
                        {open && d.photo && (
                          <img
                            src={photoUrl(d.photo, 760, 475)}
                            alt={d.alt ?? d.name}
                            width={760}
                            height={475}
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                      </span>
                    </span>
                  )}
                </>
              );

              /* Un plat sans photo n'est pas cliquable : on le rend en simple
                 ligne plutôt qu'en bouton qui ne ferait rien. */
              return expandable ? (
                <button
                  type="button"
                  className={`row${open ? " row--open" : ""}`}
                  key={d.name}
                  onClick={() => setOpenRow(open ? null : d.name)}
                  aria-expanded={open}
                >
                  {inner}
                </button>
              ) : (
                <div className="row row--static" key={d.name}>
                  {inner}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
